import { useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';

import {
  getPresignedUrl,
  uploadFileToS3,
  validateFile,
} from '@/shared/services/s3';

const VALID_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];
const MAX_DESCRIPTION_LENGTH = 1000000;

export interface QuillEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const QuillEditor = ({
  value,
  onChange,
  placeholder = 'Enter text here...',
}: QuillEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);

  const handleChange = useCallback(
    (content: string) => {
      if (content.length > MAX_DESCRIPTION_LENGTH) {
        return;
      }
      onChange(content);
    },
    [onChange],
  );

  const imageHandler = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', VALID_IMAGE_EXTENSIONS.join(','));
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
        if (!VALID_IMAGE_EXTENSIONS.includes(ext)) {
          alert(`Only ${VALID_IMAGE_EXTENSIONS.join(', ')} files are allowed`);
          return;
        }

        const validation = validateFile(file);
        if (!validation.isValid) {
          alert(validation.error);
          return;
        }

        const presignedUrl = await getPresignedUrl(file);
        const imageUrl = await uploadFileToS3(presignedUrl, file);

        const quill = quillRef.current?.getEditor();
        if (quill && imageUrl) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection({ index: range.index + 1, length: 0 });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ['clean'],
          ['link', 'image'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'color',
    'background',
    'align',
    'link',
    'image',
    'video',
    'clean',
  ];

  return (
    <div className="rich-text-editor">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default QuillEditor;
